import axios from 'axios';

/* ---------------------------------------
   AXIOS INSTANCE
--------------------------------------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true, // 🔥 REQUIRED for refresh token cookies
  headers: {
    Accept: 'application/json',
  },
});

let refreshPromise = null;
const shouldLogBackendExchange = () => process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true';
const isMutatingMethod = (method = '') => ['post', 'put', 'patch', 'delete'].includes(String(method).toLowerCase());
const safeLogData = (data) => {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const entries = {};
    data.forEach((value, key) => { entries[key] = value instanceof File ? `[File: ${value.name}]` : value; });
    return entries;
  }
  return data;
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/refresh`,
      {},
      { withCredentials: true }
    )
      .then((response) => {
        const newAccessToken = response.data?.accessToken;
        if (!newAccessToken) throw new Error('No access token returned');
        localStorage.setItem('token', newAccessToken);
        return newAccessToken;
      })
      .catch((error) => {
        // A group of simultaneous 401s must share one refresh attempt and one logout decision.
        window.dispatchEvent(new Event('auth:logout'));
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

/* ---------------------------------------
   REQUEST INTERCEPTOR
--------------------------------------- */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (shouldLogBackendExchange()) {
      try {
        const masked = token ? `${String(token).substring(0, 6)}...` : 'no-token';
        console.debug('[AXIOS DEBUG] Request ->', (config.method || '').toUpperCase(), config.url, 'Auth:', masked);
        if (isMutatingMethod(config.method)) {
          console.log('[SETTINGS/DRAWER BACKEND REQUEST]', {
            method: String(config.method || '').toUpperCase(),
            url: config.url,
            payload: safeLogData(config.data),
          });
        }
      } catch (e) {
        /* ignore logging errors */
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ---------------------------------------
   RESPONSE INTERCEPTOR (REFRESH LOGIC)
--------------------------------------- */
api.interceptors.response.use(
  (response) => {
    if (shouldLogBackendExchange() && isMutatingMethod(response.config?.method)) {
      try {
        console.log('[SETTINGS/DRAWER BACKEND RESPONSE]', {
          method: String(response.config?.method || '').toUpperCase(),
          url: response.config?.url,
          status: response.status,
          response: response.data,
        });
      } catch (e) {
        /* ignore logging errors */
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // 🚫 If no response (network error, server down)
    if (!error.response) {
      if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true') {
        console.debug('[AXIOS DEBUG] No response for request ->', originalRequest?.method?.toUpperCase(), originalRequest?.url);
      }
      return Promise.reject(error);
    }

    // 🔁 Handle expired access token
    if (
      status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true') {
        console.debug('[AXIOS DEBUG] 401 received for', originalRequest?.method?.toUpperCase(), originalRequest?.url, 'attempting refresh');
        console.debug('[AXIOS DEBUG] Response data:', error.response?.data);
      }
      try {
        const newAccessToken = await refreshAccessToken();

        if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true') {
          console.debug('[AXIOS DEBUG] Obtained refreshed access token (masked):', `${String(newAccessToken).substring(0, 6)}...`);
        }

        // Retry every request that was waiting on the same refresh with the same fresh token.
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true') {
          console.debug('[AXIOS DEBUG] Token refresh failed', refreshError);
        }
        return Promise.reject(refreshError);
      }
    }

    // ❌ Logout on 401. For 403 only logout for non-notification routes
    if (
      status === 401 ||
      (status === 403 && !String(originalRequest?.url || '').includes('/notifications'))
    ) {
      if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_AXIOS === 'true') {
        console.debug('[AXIOS DEBUG] Received', status, 'for', originalRequest?.url, 'dispatching logout');
      }
      window.dispatchEvent(new Event('auth:logout'));
    }

    return Promise.reject(error);
  }
);

export default api;
